import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  createInvitation,
  deleteInvitation as deleteInvitationDb,
  fetchInvitations,
  importLocalStorageInvitations,
  mapDbToFrontend,
  toggleFavorite as toggleFavoriteDb,
  updateInvitation as updateInvitationDb,
  updateTags as updateTagsDb,
} from '../lib/invitationService';

const defaultInvitation = {
  id: '',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  couple_names: { name1: '', name2: '' },
  date: '',
  time: '',
  time_of_day: '',
  location: '',
  city_country: '',
  event_type: 'Wedding',
  dress_code: '',
  style: 'Romantic Classic',
  tone: 'Romantic',
  theme: '',
  cultural_influence: '',
  desired_length: 'medium',
  color_preferences: [],
  style_preferences: [],
  audience_style: '',
  context_scene: '',
  extra_notes: '',
  preview_thumbnail_url: null,
  exports: [],
  favorite: false,
  tags: [],
  ai_enabled: false,
  original_inputs: null,
  selected_version_id: null,
  final_invitation_json: null,
  refinement_history: [],
};

const PLANS = {
  FREE: { name: 'Free', maxSaved: 1, price: 0 },
  ESSENTIAL: { name: 'Essential', maxSaved: 3, price: 0 },
  SIGNATURE: { name: 'Signature', maxSaved: 10, price: 5 },
  HEIRLOOM: { name: 'Heirloom', maxSaved: Infinity, price: 10 },
};

const LOCAL_STORAGE_KEY = 'vowvisions_invitations';
const LOCAL_STORAGE_PLAN_KEY = 'vowvisions_plan';
const LOCAL_STORAGE_MIGRATED_KEY = 'vowvisions_migrated';

const InvitationContext = createContext(null);

function readLocalInvitations() {
  try {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (error) {
    console.error('Failed to parse local invitation storage:', error);
    return [];
  }
}

function writeLocalInvitations(invitations) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(invitations));
}

export function InvitationProvider({ children }) {
  const { isAuthenticated } = useAuth();

  const [currentInvitation, setCurrentInvitation] = useState(defaultInvitation);
  const [savedInvitations, setSavedInvitations] = useState([]);
  const [userPlan, setUserPlan] = useState('FREE');
  const [isCrafting, setIsCrafting] = useState(false);
  const [generatedDesign, setGeneratedDesign] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPendingMigration, setHasPendingMigration] = useState(false);
  const [localInvitationsToMigrate, setLocalInvitationsToMigrate] = useState([]);

  const loadInvitations = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isAuthenticated) {
        const { data, error } = await fetchInvitations();
        if (error) {
          throw error;
        }
        setSavedInvitations((data || []).map(mapDbToFrontend).filter(Boolean));
      } else {
        setSavedInvitations(readLocalInvitations());
      }
    } catch (error) {
      console.error('Failed to load invitations:', error);
      setSavedInvitations(readLocalInvitations());
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  useEffect(() => {
    const plan = localStorage.getItem(LOCAL_STORAGE_PLAN_KEY);
    if (plan) {
      setUserPlan(plan);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(savedInvitations));
    }
  }, [savedInvitations, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const migrated = localStorage.getItem(LOCAL_STORAGE_MIGRATED_KEY);
    if (migrated === 'true') {
      return;
    }
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!saved) {
      return;
    }
    try {
      const parsed = JSON.parse(saved);
      if (parsed.length) {
        setLocalInvitationsToMigrate(parsed);
        setHasPendingMigration(true);
      }
    } catch (error) {
      console.error('Failed to parse local invitations for migration:', error);
    }
  }, [isAuthenticated]);

  const updateCurrentInvitation = (field, value) => {
    setCurrentInvitation((previous) => ({
      ...previous,
      [field]: value,
      updated_at: new Date().toISOString(),
    }));
  };

  const updateCoupleNames = (name1, name2) => {
    setCurrentInvitation((previous) => ({
      ...previous,
      couple_names: { name1, name2 },
      updated_at: new Date().toISOString(),
    }));
  };

  const loadInvitation = (idOrInvitation) => {
    const invitation = typeof idOrInvitation === 'string'
      ? savedInvitations.find((item) => item.id === idOrInvitation)
      : idOrInvitation;

    if (!invitation) {
      return null;
    }

    setCurrentInvitation(invitation);
    setGeneratedDesign(invitation.final_invitation_json || invitation.generatedDesign || null);
    return invitation;
  };

  const saveInvitationLocally = useCallback((invitationToSave) => {
    const localInvitation = {
      ...invitationToSave,
      id: invitationToSave.id || `inv_${Date.now()}`,
      created_at: invitationToSave.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _storage: 'local',
    };

    setSavedInvitations((previous) => {
      const existingIndex = previous.findIndex((item) => item.id === localInvitation.id);
      const next = existingIndex >= 0
        ? previous.map((item, index) => (index === existingIndex ? localInvitation : item))
        : [localInvitation, ...previous];

      writeLocalInvitations(next);
      return next;
    });

    setCurrentInvitation(localInvitation);
    setGeneratedDesign(localInvitation.final_invitation_json || localInvitation.generatedDesign || null);

    return {
      success: true,
      invitation: localInvitation,
      action: invitationToSave.id ? 'updated' : 'created',
      storage: 'local',
    };
  }, []);

  const removeInvitationLocally = useCallback((id) => {
    setSavedInvitations((previous) => {
      const next = previous.filter((item) => item.id !== id);
      writeLocalInvitations(next);
      return next;
    });

    if (currentInvitation.id === id) {
      resetCurrentInvitation();
    }
  }, [currentInvitation.id]);

  const updateLocalInvitationFields = useCallback((id, updates) => {
    setSavedInvitations((previous) => {
      const next = previous.map((item) => (
        item.id === id
          ? { ...item, ...updates, updated_at: new Date().toISOString(), _storage: item._storage || 'local' }
          : item
      ));

      writeLocalInvitations(next);
      return next;
    });

    if (currentInvitation.id === id) {
      setCurrentInvitation((previous) => ({
        ...previous,
        ...updates,
        updated_at: new Date().toISOString(),
        _storage: previous._storage || 'local',
      }));
    }
  }, [currentInvitation.id]);

  const saveCurrentInvitation = async () => {
    const plan = PLANS[userPlan];
    const existsInLibrary = savedInvitations.some((item) => item.id === currentInvitation.id);
    const invitationToSave = {
      ...currentInvitation,
      ai_enabled: Boolean(currentInvitation.final_invitation_json || generatedDesign),
      final_invitation_json: currentInvitation.final_invitation_json || generatedDesign || null,
      generatedDesign: currentInvitation.final_invitation_json || generatedDesign || null,
    };

    if (isAuthenticated) {
      const shouldCreate = !invitationToSave.id || !existsInLibrary;

      if (shouldCreate && savedInvitations.length >= plan.maxSaved) {
        return { success: false, reason: 'limit_reached', plan: userPlan };
      }

      const payload = shouldCreate ? { ...invitationToSave, id: '' } : invitationToSave;
      let action = shouldCreate ? 'created' : 'updated';

      let result = shouldCreate
        ? await createInvitation(payload)
        : await updateInvitationDb(invitationToSave.id, payload);

      if (result.error && result.error.code === 'network_error') {
        return saveInvitationLocally(invitationToSave);
      }

      if (result.error && shouldCreate) {
        return { success: false, reason: 'db_error', message: result.error.message };
      }

      if (result.error && !shouldCreate && /not found/i.test(result.error.message || '')) {
        if (savedInvitations.length >= plan.maxSaved) {
          return { success: false, reason: 'limit_reached', plan: userPlan };
        }

        result = await createInvitation({ ...invitationToSave, id: '' });
        action = 'created';
      } else if (result.error && !shouldCreate) {
        if (result.error.code === 'network_error') {
          return saveInvitationLocally(invitationToSave);
        }
      }

      if (result.error) {
        return { success: false, reason: 'db_error', message: result.error.message };
      }

      const mapped = mapDbToFrontend(result.data);

      await loadInvitations();
      if (mapped) {
        setCurrentInvitation(mapped);
        setGeneratedDesign(mapped.final_invitation_json || null);
      }
      return { success: true, invitation: mapped, action, storage: 'remote' };
    }

    if (!invitationToSave.id && savedInvitations.length >= plan.maxSaved) {
      return { success: false, reason: 'limit_reached', plan: userPlan };
    }

    return saveInvitationLocally(invitationToSave);
  };

  const deleteInvitation = async (id) => {
    const invitation = savedInvitations.find((item) => item.id === id);

    if (invitation?._storage === 'local') {
      removeInvitationLocally(id);
      return true;
    }

    if (isAuthenticated) {
      const { error } = await deleteInvitationDb(id);
      if (error) {
        if (error.code === 'network_error') {
          removeInvitationLocally(id);
          return true;
        }
        return false;
      }
      if (currentInvitation.id === id) {
        resetCurrentInvitation();
      }
      await loadInvitations();
      return true;
    }

    removeInvitationLocally(id);
    return true;
  };

  const toggleFavorite = async (id) => {
    const invitation = savedInvitations.find((item) => item.id === id);

    if (invitation?._storage === 'local') {
      updateLocalInvitationFields(id, { favorite: !invitation.favorite });
      return;
    }

    if (isAuthenticated) {
      const { error } = await toggleFavoriteDb(id);
      if (error) {
        if (error.code === 'network_error') {
          updateLocalInvitationFields(id, { favorite: !invitation?.favorite });
        }
        return;
      }

      await loadInvitations();
      return;
    }

    updateLocalInvitationFields(id, { favorite: !invitation?.favorite });
  };

  const updateInvitationTags = async (id, tags) => {
    const invitation = savedInvitations.find((item) => item.id === id);

    if (invitation?._storage === 'local') {
      updateLocalInvitationFields(id, { tags });
      return true;
    }

    if (isAuthenticated) {
      const { error } = await updateTagsDb(id, tags);
      if (error) {
        if (error.code === 'network_error') {
          updateLocalInvitationFields(id, { tags });
          return true;
        }
        return false;
      }
      await loadInvitations();
      return true;
    }

    updateLocalInvitationFields(id, { tags });
    return true;
  };

  const resetCurrentInvitation = () => {
    setCurrentInvitation(defaultInvitation);
    setGeneratedDesign(null);
  };

  const canSaveMore = () => savedInvitations.length < PLANS[userPlan].maxSaved;

  const getSaveLimit = () => ({
    current: savedInvitations.length,
    max: PLANS[userPlan].maxSaved,
    canSave: canSaveMore(),
  });

  const migrateLocalInvitations = async () => {
    if (!isAuthenticated || !localInvitationsToMigrate.length) {
      return { success: false, imported: 0 };
    }

    setIsLoading(true);
    try {
      const results = await importLocalStorageInvitations(localInvitationsToMigrate);
      if (results.imported > 0) {
        localStorage.setItem(LOCAL_STORAGE_MIGRATED_KEY, 'true');
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        setHasPendingMigration(false);
        setLocalInvitationsToMigrate([]);
        await loadInvitations();
      }
      return { success: true, ...results };
    } finally {
      setIsLoading(false);
    }
  };

  const skipMigration = () => {
    localStorage.setItem(LOCAL_STORAGE_MIGRATED_KEY, 'true');
    setHasPendingMigration(false);
    setLocalInvitationsToMigrate([]);
  };

  const refreshInvitations = useCallback(() => loadInvitations(), [loadInvitations]);

  return (
    <InvitationContext.Provider
      value={{
        currentInvitation,
        setCurrentInvitation,
        updateCurrentInvitation,
        updateCoupleNames,
        savedInvitations,
        saveCurrentInvitation,
        deleteInvitation,
        toggleFavorite,
        updateInvitationTags,
        loadInvitation,
        resetCurrentInvitation,
        userPlan,
        setUserPlan,
        canSaveMore,
        getSaveLimit,
        PLANS,
        isCrafting,
        setIsCrafting,
        generatedDesign,
        setGeneratedDesign,
        isLoading,
        refreshInvitations,
        hasPendingMigration,
        localInvitationsToMigrate,
        migrateLocalInvitations,
        skipMigration,
      }}
    >
      {children}
    </InvitationContext.Provider>
  );
}

export const useInvitation = () => {
  const context = useContext(InvitationContext);
  if (!context) {
    throw new Error('useInvitation must be used within InvitationProvider');
  }
  return context;
};

export { PLANS, defaultInvitation };
